import classNames from "classnames"
import React, { useRef, useState } from "react"

import { sleep } from "@bounce/util"

import { useOnClickOutside } from "../../../hooks/useOnClickOutside"
import type { FormInputProps } from "../FormInput/FormInput"
import { FormInput } from "../FormInput/FormInput"
import type { PickerProps } from "../Picker/Picker"
import { Picker } from "../Picker/Picker"
import type { PickerOption } from "../Picker/PickerItem"

export type FormSelectProps = Omit<FormInputProps, "type"> & {
  options?: PickerProps["options"]
  onClickOption?: PickerProps["onClick"]
}

export const FormSelect = ({
  options = [],
  onClickOption,
  onChange,
  className,
  size = "md",
  ...formInputProps
}: FormSelectProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isOpen, setOpen] = useState(false)

  useOnClickOutside({
    ref,
    handler: async () => {
      // Close with delay to capture the recommendations click
      await sleep(500)
      setOpen(false)
    },
  })

  const onClick = (option: PickerOption) => {
    onClickOption?.(option)
    setOpen(false)
  }

  const onValueChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(ev)
    setOpen(true)
  }

  return (
    <div ref={ref} className={classNames("form-select", className)}>
      <FormInput {...formInputProps} onChange={onValueChange} size={size} autoComplete="off" type="text" />
      {isOpen && <Picker options={options} onClick={onClick} className="form-select__picker" />}
    </div>
  )
}
